import { Log } from '../AuxillaryService/Log';
import { glom } from './glom';
import * as Platform from '../Platform';
import { Permission } from '../Platform/Permissions';
import { ReachAPI } from '../ReachApi';
import { initializeAuxillaryServices } from '../AuxillaryService';
import { createDisplays, validateDisplayConfiguration } from '../Display';
import { $ } from '../../vendor/jquery';
import { moment } from '../../vendor/moment';
import { initializeMessagingClient } from '../MessagingClient';
import { NamedEventCode } from '@reachmedianetwork/signage-abstractions';
import { getPlayerUuid, getPreviewIds, getVersion } from './utility';
import { createInactiveDisplay } from './inactive';
import { addGlobalHotkeys } from './GlobalHotkeys';
import { initializeFeatureEnrollment, initializeLabOptions } from './initFunctions';

// eslint-disable-next-line no-unused-vars
import { platform, PlatformInterface, supportsTimeZone } from '../Platform';
import { signageRebootSystem } from '.';

// WARNING: `REACH` should not be called outside of a `Platform` module.
// TODO: move `REACH` calls into `ReachPlayer`.
/* global REACH */

/**
 * @class
 * @hideconstructor
 */
const Signage = {};

/**
 * private promise, resolved when lab options are ready.
 */
var _signageReadyResolveFunction = null;
var _signageReadyRejectFunction = null;
var _signageReadyPromise = new Promise(function (resolve, reject) {
    _signageReadyResolveFunction = resolve;
    _signageReadyRejectFunction = reject;
});

/**
 * @typedef {Object} SystemData
 * @property {string} userAgent
 * @property {string} [platform]
 * @property {string} language
 * @property {Object} resolutions
 * @property {Object} resolutions.screen
 * @property {number} resolutions.screen.width
 * @property {number} resolutions.screen.height
 * @property {Object} resolutions.window
 * @property {number} resolutions.window.width
 * @property {number} resolutions.window.height
 * @property {Object} resolutions.document
 * @property {number} resolutions.document.width
 * @property {number} resolutions.document.height
 * @property {Object} browserStorageEstimate
 * @property {number} [browserStorageEstimate.usage]
 * @property {number} [browserStorageEstimate.quota]
 * @property {Object} signage
 * @property {string} signage.version
 * @property {number} signage.uptime - Uptime in milliseconds
 * @property {number} signage.initializationCount
 * @property {Object} player
 * @property {string} player.version
 * @property {number} player.platformCode
 * @property {string} player.platform
 * @property {number} player.timelineId
 * @property {Object} [brightsign]
 * @property {string} brightsign.family
 * @property {string} brightsign.deviceUniqueId
 * @property {string} brightsign.bootVersion
 * @property {number} brightsign.deviceBootCount
 * @property {number} brightsign.deviceLifetime
 * @property {number} brightsign.deviceUptime
 * @property {string} brightsign.version
 * @property {string} brightsign.model
 */

//START: Populate From API
// <editor-fold desc="Populate From API">
Signage.isPreview = null;

Signage.playerUuid = null;
Signage.securityString = null;
Signage.displayId = null;

/**
 * @type {Display[]}
 * @deprecated use {@link getDisplays} instead
 */
Signage.displays = null;
Signage.schedules = null;

Signage.timelineId = null;

Signage.width = null;
Signage.height = null;
Signage.orientation = null;

Signage.alertConfigurationId = null;
Signage.weatherAlertConfigurationId = null;
Signage.isPaused = null;
Signage.localTimeZone = null;

Signage.platformUpdateEnabled = null;
Signage.platformUpdateDay = null;
Signage.platformUpdateTime = null;
Signage.lastPlatformUpdateDate = null;

Signage.enabledProofOfPlay = null;

Signage.ntpEnabled = null;
Signage.ntpServer = null;
Signage.ntpClockSkew = null;

Signage.dailyRestartBegin = null;
Signage.dailyRestartEnd = null;
Signage.dailyReboot = null;

Signage.title = null;

Signage.caching = null;
// </editor-fold desc="Populate From API">
//END: Populate From API

Signage.startTime = null;
Signage.thirdPartyApps = null;
Signage.comPort = null;
Signage.displayMake = null;

Signage.isInactive = null;

Signage.facilityStatus = null;

Signage.initializationPromise = null;

/**
 * The number of times the Signage has initialized
 * @type {number}
 */
Signage.initializationCount = 0;

/**
 * @type {PlatformInterface}
 */
Signage.platform = Signage.platform || Platform.platform();

/**
 * Initialize Signage (Populate global variable and append displays/zones
 * elements)
 * @throws {Error}
 * @throws {ReachApiError}
 * @returns {Promise}
 *      Success `Promise`  <br />
 *      Failure `Promise[Error]`  <br />
 *      See {@link ReachAPI.signage.getSignageConfiguration} and
 *      {@link ReachAPI.signage.getTemplate} for more information about
 *      promises.
 */
Signage.Initialize = function () {
    var playerUuid = getPlayerUuid();
    var previewIds = getPreviewIds();
    var securityToken = previewIds ? previewIds.securityToken : null;
    var displayId = previewIds ? previewIds.displayId : null;
    var permissionsV2 = previewIds ? previewIds.permissionsV2 : null;

    var security = new ReachAPI.Security(playerUuid, securityToken);
    var initializationCountKey = 'data__initialization_count';

    // Set Start Time
    Signage.startTime = Date.now();

    // Initialize Auxillary Services including logger mechanisms
    initializeAuxillaryServices();

    // Close all COM ports if they are open (todo: move to
    // `ReachPlayer#initializePlatform()`)
    if (typeof REACH !== 'undefined' && REACH.ComPort) {
        REACH.ComPort.CloseAllComPorts();
    }

    addGlobalHotkeys();

    return Promise.resolve()
        .then(() => {
            if (Platform.supportsLogConfiguration(Signage.platform)) {
                return Signage.platform.configureLogging(Log);
            }
        })
        .then(
            function () {
                // increment initialization count
                return Promise.resolve()
                    .then(function () {
                        return Signage.platform.getNamedValue(initializationCountKey, '0');
                    })
                    .then(function (count) {
                        count = (parseInt(count) || 0) + 1;
                        Signage.initializationCount = count;

                        return Signage.platform.setNamedValue(initializationCountKey, count);
                    })
                    .catch(function (error) {
                        Log.Error(error.toString(), null, __fileline);
                    });
            }.bind(this)
        )
        .then(
            function () {
                // retrieve Signage configuration and display/layout template in
                // parallel
                var configPromise = ReachAPI.deferredToPromise(
                    ReachAPI.signage.getSignageConfiguration(security, displayId, permissionsV2)
                );
                return Promise.all([configPromise]);
            }.bind(this)
        )
        .then(async (resolves) => {
            // Configure `Signage` using data retrieved from the API
            var configuration = resolves[0].data;
            // Use data retrieved from the API to Populate Member Variables
            _populateMemberVariables(configuration);

            if (Signage.enabledProofOfPlay) {
                Signage.platform.addPermission(Permission.FORWARD_PROOF_OF_PLAY);
            }

            await initializeFeatureEnrollment(platform(), configuration.features);
        })
        .then(Signage.initializeTimezoneSettings.bind(this))
        .then(Signage.platform.getHardwareId.bind(this.platform))
        .then(initializeMessagingClient)
        .then(
            function () {
                var jqDeferredsToWaitOn = [];

                // populate Lab Options
                var jqDeferredLabOptionsInitialized = initializeLabOptions(platform());

                jqDeferredsToWaitOn.push(jqDeferredLabOptionsInitialized);

                // set `<title>`
                document.title = Signage.title;

                // populate global variables
                glom.playerUuid = Signage.playerUuid;
                glom.securityToken = Signage.securityString;

                glom.timezone = Signage.localTimeZone;
                glom.caching = Signage.caching;

                glom.playerXDimension = Signage.width;
                glom.playerYDimension = Signage.height;

                glom.alertConfigId =
                    process.env.EMERGENCY_ALERTS_TEST_ID ?? Signage.alertConfigurationId;
                glom.weatherAlertConfigurationId =
                    process.env.WEATHER_ALERTS_TEST_ID ?? Signage.weatherAlertConfigurationId;

                glom.dailyRestartWindow.begin = Signage.dailyRestartBegin;
                glom.dailyRestartWindow.end = Signage.dailyRestartEnd;

                Signage.RegisterDisplayUserScripts(Signage.displays);

                Signage.LaunchThirdPartyApps(Signage.thirdPartyApps);

                var jqDeferredPlatformInitialized = Signage.platform.initializePlatform();
                jqDeferredsToWaitOn.push(jqDeferredPlatformInitialized);

                jqDeferredsToWaitOn.push(
                    jqDeferredPlatformInitialized.then(async () => {
                        if (!Signage.isInactive) {
                            const validDisplays = Signage.displays.map((d) =>
                                validateDisplayConfiguration(d)
                            );
                            Signage.displays = createDisplays(validDisplays, 'body');
                        } else {
                            //player has been soft deleted or facility is inactive
                            await createInactiveDisplay(Signage.facilityStatus, 'body');
                        }
                    })
                );

                $.when.apply($, jqDeferredsToWaitOn).then(
                    () => {
                        Log.NamedEvent(
                            NamedEventCode.SIGNAGE_BOOTSTRAP,
                            'signage initialization started',
                            __fileline,
                            {
                                success: true,
                                startTime: Signage.startTime,
                                endTime: Date.now(),
                                initializationCount: Signage.initializationCount,
                            }
                        );

                        _signageReadyResolveFunction();
                    },
                    (e) => {
                        Log.NamedEvent(
                            NamedEventCode.SIGNAGE_BOOTSTRAP,
                            'signage initialization started',
                            __fileline,
                            {
                                success: false,
                                error: e,
                                startTime: Signage.startTime,
                                endTime: Date.now(),
                                initializationCount: Signage.initializationCount,
                            }
                        );

                        _signageReadyRejectFunction(e);
                    }
                );
            }.bind(this)
        );
};

/**
 * Returns a promise that is resolved or rejected when Signage is ready for use
 * or has failed to initialize
 * @todo clean up initialization logic: Signage.ready function (and supporting
 * variables) are not useful. Remove.
 * @returns Promise
 */
Signage.ready = function () {
    return _signageReadyPromise;
};

/**
 * Returns an estimate of the browser's available storage (in bytes)
 * @see {@link https://developers.google.com/web/updates/2017/08/estimating-available-storage-space}
 * @returns {Promise<{usage: number, quota: number}>|Promise<Error>}
 */
Signage.browserStorageEstimate = function () {
    var jqDeferred = $.Deferred();

    if (
        typeof navigator.storage !== 'undefined' &&
        typeof navigator.storage.estimate !== 'undefined'
    ) {
        Log.Debug('Estimate Storage: Standards', null, __fileline);
        navigator.storage
            .estimate()
            .then(function (estimate) {
                jqDeferred.resolve({
                    usage: estimate.usage,
                    quota: estimate.quota,
                });
            })
            .catch(function (e) {
                jqDeferred.reject(e);
            });
    } else if (
        typeof navigator.webkitTemporaryStorage !== 'undefined' &&
        typeof navigator.webkitTemporaryStorage.queryUsageAndQuota !== 'undefined'
    ) {
        Log.Debug('Estimate Storage: WebKit', null, __fileline);
        navigator.webkitTemporaryStorage.queryUsageAndQuota(
            function (usedBytes, grantedBytes) {
                jqDeferred.resolve({
                    usage: usedBytes,
                    quota: grantedBytes,
                });
            },
            function (e) {
                jqDeferred.reject(e);
            }
        );
    } else {
        Log.Debug('Estimate Storage: Not Supported', null, __fileline);
        jqDeferred.reject(new Error('Estimating available storage is not supported'));
    }

    return jqDeferred;
};

/**
 *  Called to run QUnit tests from 'display_user_scripts' function.
 *
 * @param callback = user script function stored in the database
 */
Signage.loadQunit = function (callback) {
    var tag = document.createElement('script');

    tag.src = '/common/js/QUnit/qunit-2.3.2.js';
    var firstScriptTag = document.getElementsByTagName('script')[0];

    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.reachQUnitLoaded = function () {
        setTimeout(callback, 9000, Signage);
    };
};

/**
 * registers user scripts for each display with player software
 * @todo move to {@link ReachPlayer}
 */
Signage.RegisterDisplayUserScripts = function (displays) {
    if (typeof REACH !== 'undefined' && REACH.CEF && REACH.CEF.RegisterUserScript) {
        REACH.CEF.ClearUserScripts();

        for (var displayId in displays) {
            displays[displayId].user_scripts.forEach(function (userScript) {
                try {
                    REACH.CEF.RegisterUserScript(userScript.urls, userScript.script);
                } catch (e) {
                    Log.Error(
                        'Failed to register user script `' + userScript.name + '`',
                        null,
                        __fileline,
                        {
                            message: e,
                        }
                    );
                }
            });
        }
    }
};

/**
 * @todo move to {@link ReachPlayer}
 */
Signage.LaunchThirdPartyApps = function (thirdPartyApps) {
    if (typeof REACH !== 'undefined' && REACH.Application && REACH.Application.AddThirdPartyApp) {
        for (var i in thirdPartyApps) {
            var app = thirdPartyApps[i];
            REACH.Application.AddThirdPartyApp(
                app.path,
                app.arguments,
                app.single_instance == 'true',
                app.termination_mode,
                app.hide_window == 'true',
                app.background_audio == 'true',
                app.exe_launcher_name,
                10 * 1000
            );
        }
    } else {
        if (thirdPartyApps && thirdPartyApps.length > 0) {
            Log.Warning(
                'Could not launch third party app(s): not implemented on this player',
                null,
                __fileline
            );
        }
    }
};

/**
 * Get the number of milliseconds that the Signage has been running
 * @return {number}  A Number representing the milliseconds elapsed since the
 * Signage started
 */
Signage.GetUptime = function () {
    return Date.now() - Signage.startTime;
};

/**
 * Parse CMS time zone settings, set default time zone on moment, and attempt to
 * set platform time zone
 * @returns {Promise|Promise<void>}
 */
Signage.initializeTimezoneSettings = function () {
    var timeZoneIsNotDefined = !this.localTimeZone || this.localTimeZone === 'auto';

    if (timeZoneIsNotDefined) {
        // determine time zone
        this.localTimeZone = moment.tz.guess();
        Log.Notice(
            'Time zone was not set, determined to be `' + this.localTimeZone + '`',
            null,
            __fileline
        );
        return Promise.resolve();
    } else {
        // set time zone if defined
        if (moment.tz.zone(this.localTimeZone)) {
            moment.tz.setDefault(this.localTimeZone);
        } else {
            this.localTimeZone = moment.tz.guess();
            Log.Warning(
                'Invalid time zone provided.  Defaulted to ' + this.localTimeZone,
                null,
                __fileline
            );
        }

        return Signage.setTimeZone(this.localTimeZone);
    }
};

/**
 * Return `moment` object set to Signage's timezone
 * @see {@link http://momentjs.com/}
 * @deprecated
 * @param {...*}
 * @return {Moment} - `moment` object set to Signage's timezone
 */
Signage.localMoment = function () {
    var argsArray = [].slice.call(arguments); // convert `arguments` to array
    argsArray.push(this.localTimeZone); // add Signage's timezone to end of arguments
    return moment.tz.apply(this, argsArray); // return `moment` object set to Signage's timezone
};

/**
 * Return `moment` object for current day's time set to Signage's timezone.
 *
 * (Note: This is a workaround for a bug in `moment.tz()` where parsing a
 * time-only string will return the next day's time when executed after 5pm PST)
 * @deprecated
 * @param {string} timeString
 * @param {string} [format='h:mm a']
 * @param {boolean} [strict_parsing=false]
 * @return {Moment}
 */
Signage.localMomentTime = function (timeString, format, strict_parsing) {
    format = format || 'h:mm a';
    strict_parsing = strict_parsing || false;
    var momentTimeString = this.localMoment().format('YYYYMMDD') + ' ' + timeString;
    return this.localMoment(momentTimeString, 'YYYYMMDD ' + format, strict_parsing);
};

/**
 * Post System Configuration to the API
 *
 * Currently only sends data if running on a BrightSign, FireOSPlayer,
 * ChromeExtensionPlayer, PWAPlayer or WebOSPlayer unit since the REACH player
 * software has this built-in and there is no need to send this information
 * when running in a browser.
 *
 */
Signage.PostSystemConfiguration = function () {
    if (typeof Signage.platform.getSystemInformation === 'function') {
        Signage.platform.getSystemInformation().then(function (systemData) {
            if (
                !$.isEmptyObject(systemData.HardwareInfo) ||
                !$.isEmptyObject(systemData.OperatingSystemInfo)
            ) {
                Log.Info('Sending System Configuration', null, __fileline);
                ReachAPI.player.postReportSystemConfig(Signage.playerUuid, systemData);
            }
        });
    } else {
        if (!Signage.isPreview) {
            Signage.SystemInformation().then(function (systemInfo) {
                var systemData = { HardwareInfo: {}, OperatingSystemInfo: {} };

                //TODO: use something like
                //[UAParser.js](https://faisalman.github.io/ua-parser-js/) to
                //extract info from the user-agent

                if (typeof systemInfo.brightsign == 'object') {
                    systemData.HardwareInfo.Manufacturer = systemInfo.player.platform;
                    systemData.HardwareInfo.Model = systemInfo.brightsign.model;

                    systemData.OperatingSystemInfo.Name = systemInfo.player.platform;
                    systemData.OperatingSystemInfo.Manufacturer = systemInfo.player.platform;
                    systemData.OperatingSystemInfo.Version = systemInfo.brightsign.version;
                }

                if (
                    !$.isEmptyObject(systemData.HardwareInfo) ||
                    !$.isEmptyObject(systemData.OperatingSystemInfo)
                ) {
                    Log.Info('Sending System Configuration', null, __fileline);
                    ReachAPI.player.postReportSystemConfig(Signage.playerUuid, systemData);
                }
            });
        }
    }
};

/**
 * Set host time zone (if host platform supports it)
 * @param {string} timeZone
 * @param {boolean} [autoReboot=true] - Reboot player if required to apply new
 * time zone
 * @returns {Promise} (This should never fail)
 */
Signage.setTimeZone = function (timeZone, autoReboot) {
    if (typeof autoReboot === 'undefined') {
        autoReboot = true;
    }

    if (!supportsTimeZone(Signage.platform)) {
        Log.Warning('Host platform does not support changing the time zone.', null, __fileline);
        return Promise.resolve();
    }

    return Promise.resolve()
        .then(function () {
            Log.Info('Setting system time zone to `' + timeZone + '`', null, __fileline);
            return Signage.platform.setTimeZone(timeZone);
        })
        .then(function (response) {
            if (response.rebootRequired === true) {
                Log.Info('A reboot is required to apply the new time zone.', null, __fileline);
                if (autoReboot === true) {
                    Log.Info('Rebooting now.', null, __fileline);
                    void signageRebootSystem();
                }
            }
        })
        .catch(function (error) {
            Log.Error('Failed to set system time zone', null, __fileline, error);
        });
};

/**
 * Return object containing system information
 * @returns {Promise<SystemData>} systemData
 */
Signage.SystemInformation = function () {
    var systemData = {};
    var browserStorageEstimatePromise;
    var hostPlatformPromise;
    var playerVersionPromise;
    var brightsignInfoPromise;
    var jqDeferred = $.Deferred();

    // system
    systemData.userAgent = navigator.userAgent;
    systemData.platform = undefined;
    systemData.language = navigator.language;
    hostPlatformPromise = Signage.platform.getHostPlatform().then(
        function (hostPlatform) {
            systemData.platform = hostPlatform;
        },
        function (error) {
            Log.Warning(
                "Failed to get the host's platform type: " + error.toString(),
                null,
                __fileline,
                { error: error }
            );
        }
    );

    // resolutions
    systemData.resolutions = {};
    systemData.resolutions.screen = {};
    systemData.resolutions.screen.width = screen.width;
    systemData.resolutions.screen.height = screen.height;
    systemData.resolutions.window = {};
    systemData.resolutions.window.width = window.innerWidth;
    systemData.resolutions.window.height = window.innerHeight;
    systemData.resolutions.document = {};
    systemData.resolutions.document.width = Math.max(
        document.body.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth
    );
    systemData.resolutions.document.height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
    );

    // browser
    systemData.browserStorageEstimate = {};
    browserStorageEstimatePromise = Signage.browserStorageEstimate().then(
        function (storageEstimate) {
            systemData.browserStorageEstimate = {
                usage: storageEstimate.usage,
                quota: storageEstimate.quota,
            };
        },
        function (error) {
            Log.Warning(
                "Failed to get estimate of the browser's available storage: " + error.toString(),
                null,
                __fileline,
                { error: error }
            );
        }
    );

    // signage
    systemData.signage = {};
    systemData.signage.version = getVersion();
    systemData.signage.uptime = Signage.GetUptime();
    systemData.signage.initializationCount = Signage.initializationCount;

    // player
    systemData.player = {};
    systemData.player.version = 'unknown';
    systemData.player.platformCode = Signage.platform.type;
    systemData.player.platform = Signage.platform.name;
    systemData.player.timelineId = Signage.timelineId;
    playerVersionPromise = Signage.platform.getPlayerVersion().then(
        function (playerVersion) {
            systemData.player.version = playerVersion;
        },
        function (error) {
            Log.Warning(
                "Failed to get player's version number: " + error.toString(),
                null,
                __fileline,
                { error: error }
            );
        }
    );

    // special
    if (Signage.platform.getBsDeviceInfo !== undefined) {
        brightsignInfoPromise = Signage.platform.getBsDeviceInfo().then(
            function (brightsignInfo) {
                systemData.brightsign = {
                    family: brightsignInfo.family,
                    deviceUniqueId: brightsignInfo.deviceUniqueId,
                    bootVersion: brightsignInfo.bootVersion,
                    deviceBootCount: brightsignInfo.deviceBootCount,
                    deviceLifetime: brightsignInfo.deviceLifetime,
                    deviceUptime: brightsignInfo.deviceUptime,
                    version: brightsignInfo.version,
                    model: brightsignInfo.model,
                };
            },
            function (error) {
                Log.Warning(
                    'Failed to get BrightSign device information: ' + error.toString(),
                    null,
                    __fileline,
                    { error: error }
                );
            }
        );
    } else {
        brightsignInfoPromise = Promise.resolve();
    }

    $.when(
        browserStorageEstimatePromise,
        playerVersionPromise,
        hostPlatformPromise,
        brightsignInfoPromise
    ).always(function () {
        jqDeferred.resolve(systemData);
    });

    return jqDeferred;
};

/**
 * Populate member variables from given data, but only populate the variables
 * currently set to null
 * @param {object} data
 * @private
 */
var _populateMemberVariables = function (data) {
    for (var key in data) {
        // convert legal php snake_case variable to corresponding camelCase
        // variable
        var camelCaseKey = key.replace(/_([a-z0-9])/g, function (match, letter) {
            return letter.toUpperCase();
        });
        if (Signage[camelCaseKey] !== undefined && Signage[camelCaseKey] === null) {
            Signage[camelCaseKey] = data[key];
        }
    }
};

export { Signage };
